import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { ProductNotificationStatus } from "@redotech/redo-customer-common/customer-product-notification/customer-product-notification";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import ChannelIcon from "@redotech/redo-icons/dataflow-04.svg";
import FilterIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import SearchIcon from "@redotech/redo-icons/search-sm_filled.svg";
import StatusIcon from "@redotech/redo-icons/status.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoDateRangePickerDropdown } from "@redotech/redo-web/arbiter-components/date-picker/redo-date-range-picker-dropdown";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
  RedoFilterSection,
} from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { SimpleMultiSelectFilterDropdown } from "@redotech/redo-web/filters/simple-multiselect-filter-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { getDateString } from "@redotech/util/date-format";
import { memo, useState, type ReactNode } from "react";
import * as styles from "./bis.module.css";

type BisFilter = "date" | "channel" | "status";

export const SearchAndFilterBar = memo(function SearchAndFilterBar({
  searchQuery,
  setSearchQuery,
  placeholder,
  dateFilter,
  setDateFilter,
  channelFilters,
  setChannelFilters,
  statusFilters,
  setStatusFilters,
  trailingButtons,
}: {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  placeholder: string;
  dateFilter: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateFilter: (
    d: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  channelFilters: MarketingChannel[] | undefined;
  setChannelFilters: (c: MarketingChannel[] | undefined) => void;
  statusFilters: ProductNotificationStatus[] | undefined;
  setStatusFilters: (s: ProductNotificationStatus[] | undefined) => void;
  trailingButtons?: ReactNode;
}) {
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterButtonRef, setFilterButtonRef] = useState<HTMLElement | null>(
    null,
  );
  const [isDateFilterActive, setIsDateFilterActive] = useState(
    dateFilter !== undefined,
  );

  const hasDateFilter = isDateFilterActive || dateFilter !== undefined;
  const hasChannelFilter = channelFilters !== undefined;
  const hasStatusFilter = statusFilters !== undefined;
  const hasAnyFilter = hasDateFilter || hasChannelFilter || hasStatusFilter;

  const isFilterActive: Record<BisFilter, boolean> = {
    date: hasDateFilter,
    channel: hasChannelFilter,
    status: hasStatusFilter,
  };

  const selectedFilterItems = FILTER_OPTIONS.filter(
    (o) => isFilterActive[o.value],
  );

  const addFilter = (filter: BisFilter) => {
    switch (filter) {
      case "channel":
        setChannelFilters([MarketingChannel.EMAIL, MarketingChannel.SMS]);
        break;
      case "status":
        setStatusFilters([
          ProductNotificationStatus.WAITING,
          ProductNotificationStatus.NOTIFIED,
        ]);
        break;
      case "date":
        setIsDateFilterActive(true);
        break;
    }
  };

  const removeFilter = (filter: BisFilter) => {
    switch (filter) {
      case "date":
        setIsDateFilterActive(false);
        setDateFilter(undefined);
        break;
      case "channel":
        setChannelFilters(undefined);
        break;
      case "status":
        setStatusFilters(undefined);
        break;
    }
  };

  return (
    <Flex dir="column" gap="md">
      <Flex align="stretch" dir="row" gap="sm">
        <Flex flex={1}>
          <RedoTextInput
            fullWidth
            IconLeading={SearchIcon}
            placeholder={placeholder}
            setValue={setSearchQuery}
            size="sm"
            value={searchQuery}
          />
        </Flex>
        <RedoButton
          aria-label="Filter"
          hierarchy="secondary"
          IconLeading={FilterIcon}
          onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
          ref={setFilterButtonRef}
        />
        <RedoMultiselectDropdown
          dropdownAnchor={filterButtonRef}
          dropdownOpen={filterDropdownOpen}
          items={FILTER_OPTIONS}
          selectedItems={selectedFilterItems}
          setDropdownOpen={setFilterDropdownOpen}
          setSelectedItems={(items) => {
            const newFilters = new Set(items.map((item) => item.value));
            for (const filter of FILTER_OPTIONS) {
              if (
                newFilters.has(filter.value) &&
                !isFilterActive[filter.value]
              ) {
                addFilter(filter.value);
              } else if (
                !newFilters.has(filter.value) &&
                isFilterActive[filter.value]
              ) {
                removeFilter(filter.value);
              }
            }
          }}
        />
        {trailingButtons}
      </Flex>
      {hasAnyFilter && (
        <Flex className={styles.compactFilters} dir="row" gap="sm" wrap="wrap">
          {hasDateFilter && (
            <DateFilterChip
              dateRange={dateFilter}
              removeFilter={() => removeFilter("date")}
              setDateRange={setDateFilter}
            />
          )}
          {hasChannelFilter && (
            <RedoFilterGroup
              Icon={ChannelIcon}
              propertyName="Channel"
              query={<RedoFilterSection text="is any of" />}
              removeFilter={() => removeFilter("channel")}
              value={
                <SimpleMultiSelectFilterDropdown<MarketingChannel>
                  filterStyle="value"
                  options={[MarketingChannel.EMAIL, MarketingChannel.SMS]}
                  optionToFriendlyName={(o) =>
                    ({
                      [MarketingChannel.EMAIL]: "Email",
                      [MarketingChannel.SMS]: "SMS",
                    })[o]
                  }
                  selectedOptionsToDisplay={formatSelectedOptions}
                  setValues={setChannelFilters}
                  values={channelFilters}
                />
              }
            />
          )}
          {hasStatusFilter && (
            <RedoFilterGroup
              Icon={StatusIcon}
              propertyName="Status"
              query={<RedoFilterSection text="is" />}
              removeFilter={() => removeFilter("status")}
              value={
                <SimpleMultiSelectFilterDropdown
                  filterStyle="value"
                  options={[
                    ProductNotificationStatus.WAITING,
                    ProductNotificationStatus.NOTIFIED,
                  ]}
                  optionToFriendlyName={(o) =>
                    ({
                      [ProductNotificationStatus.WAITING]: "Waiting",
                      [ProductNotificationStatus.NOTIFIED]: "Notified",
                    })[o]
                  }
                  selectedOptionsToDisplay={formatSelectedOptions}
                  setValues={setStatusFilters}
                  values={statusFilters}
                />
              }
            />
          )}
        </Flex>
      )}
    </Flex>
  );
});

const FILTER_OPTIONS = [
  {
    id: "date",
    type: "text" as const,
    text: "Subscribe date",
    value: "date" as const,
  },
  {
    id: "channel",
    type: "text" as const,
    text: "Channel",
    value: "channel" as const,
  },
  {
    id: "status",
    type: "text" as const,
    text: "Status",
    value: "status" as const,
  },
];

const DateFilterChip = memo(function DateFilterChip({
  dateRange,
  setDateRange,
  removeFilter,
}: {
  dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateRange: (
    d: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  removeFilter: () => void;
}) {
  const [valueRef, setValueRef] = useState<HTMLButtonElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const valueText = dateRange
    ? `${getDateString(dateRange[0])} - ${getDateString(dateRange[1])}`
    : "Select dates";

  return (
    <>
      <RedoDateRangePickerDropdown
        anchorEl={valueRef}
        dateRange={dateRange}
        onClose={() => setPickerOpen(false)}
        open={pickerOpen}
        setDateRange={setDateRange}
      />
      <RedoFilterGroup
        Icon={CalendarIcon}
        propertyName="Subscribe date"
        query={<RedoFilterSection color="secondary" text="within" />}
        removeFilter={removeFilter}
        value={
          <RedoFilterDropdownAnchor
            color="primary"
            onClick={() => setTimeout(() => setPickerOpen((prev) => !prev), 0)}
            ref={setValueRef}
            text={valueText}
            weight="medium"
          />
        }
      />
    </>
  );
});

function formatSelectedOptions(options: string[] | undefined): string {
  const count = options?.length ?? 0;
  if (count === 1) {
    return options![0]!;
  }
  return `${count} selected`;
}
